import React, { useState, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  type SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useToast } from '../contexts/ToastContext';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { STAFF_PERMISSIONS_ROLE_TYPES, STAFF_FEATURES, resolveAuthorityLevelsFromSchool } from '../constants';
import type { SchoolItem, SchoolsResponse } from '../types';
import {
  type StaffFeaturePermissionBlock,
  type StaffPermissionsMode,
  loadEditorFromSchool,
  emptyBlock,
  defaultBlocksAfterModeSwitch,
  buildStaffPermissionsSaveSettings,
  mergeFeaturesForSelection,
} from '../utils/staffPermissionBlocks';

const ACCESS_OPTIONS = [
  { value: 'ALL', label: 'All students' },
  { value: 'CLASS_ONLY', label: 'Class / section only (assigned)' },
  { value: 'ASSIGNED_ONLY', label: 'Assigned students only' },
  { value: 'NONE', label: 'No access' },
];

const StaffRolePermissions: React.FC = () => {
  const queryClient = useQueryClient();
  const user = authService.getStoredUser();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('staffRolePermissions');
  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  const [mode, setMode] = useState<StaffPermissionsMode>('ROLE');
  const [blocks, setBlocks] = useState<StaffFeaturePermissionBlock[]>([]);

  React.useEffect(() => {
    if (!school) return;
    const loaded = loadEditorFromSchool(school);
    setMode(loaded.mode);
    setBlocks(loaded.blocks);
  }, [school]);

  const authorityOptions = useMemo(
    () => resolveAuthorityLevelsFromSchool(school?.settings?.authorityLevels),
    [school?.settings?.authorityLevels],
  );

  const requestModeChange = (_: React.MouseEvent<HTMLElement>, next: StaffPermissionsMode | null) => {
    if (next == null || next === mode) return;
    const ok = window.confirm(
      [
        'Switch permission mode?',
        '',
        '• This resets the page to a single empty permission group.',
        '• When you Save, only the selected mode is kept — the other mode\'s saved permissions are cleared so they cannot conflict.',
        '• Unsaved edits are lost now unless you cancel.',
      ].join('\n'),
    );
    if (!ok) return;
    setMode(next);
    if (school) {
      setBlocks(defaultBlocksAfterModeSwitch(next, school));
    }
  };

  const addBlock = () => {
    if (!school) return;
    setBlocks((prev) => [...prev, emptyBlock(mode, school)]);
  };

  const removeBlock = (blockId: string) => {
    setBlocks((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((b) => b.id !== blockId);
    });
  };

  const updateBlockKeys = useCallback(
    (blockId: string, keys: string[]) => {
      if (!school) return;
      setBlocks((prev) => {
        const keySet = new Set(keys);
        return prev.map((b) => {
          if (b.id === blockId) {
            return {
              ...b,
              selectedKeys: keys,
              features: mergeFeaturesForSelection(b.features, mode, keys, school.settings),
            };
          }
          return { ...b, selectedKeys: b.selectedKeys.filter((k) => !keySet.has(k)) };
        });
      });
    },
    [mode, school],
  );

  const updateMutation = useMutation(
    async (payload: SchoolItem) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Staff permissions saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = () => {
    if (!school) return;
    if (blocks.length === 0) {
      showToast('error', 'Add at least one permission group before saving.');
      return;
    }
    const nextSettings = buildStaffPermissionsSaveSettings(school, mode, blocks);
    const payload = {
      ...school,
      settings: nextSettings,
    };
    updateMutation.mutate(payload as SchoolItem);
  };

  const renderKeySelect = (block: StaffFeaturePermissionBlock) => {
    if (!school) return null;
    const label = mode === 'ROLE' ? 'Roles in this group' : 'Authority levels in this group';
    const options: { value: string; label: string }[] =
      mode === 'ROLE'
        ? STAFF_PERMISSIONS_ROLE_TYPES.map(({ value, label: lb }) => ({ value, label: lb }))
        : authorityOptions.map((l) => ({ value: l.value, label: l.label }));

    const handleChange = (e: SelectChangeEvent<string[]>) => {
      const v = e.target.value;
      const keys = typeof v === 'string' ? v.split(',') : v;
      updateBlockKeys(block.id, keys);
    };

    return (
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id={`${block.id}-keys`}>{label}</InputLabel>
        <Select
          labelId={`${block.id}-keys`}
          multiple
          value={block.selectedKeys}
          onChange={handleChange}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) =>
            (selected as string[])
              .map((v) => options.find((o) => o.value === v)?.label || v)
              .join(', ')
          }
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox checked={block.selectedKeys.indexOf(o.value) > -1} size="small" />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  if (!user || !school) return <Box><Typography>Loading...</Typography></Box>;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1 }}>
        Staff Permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose whether feature access is driven by <strong>job role</strong> or by <strong>authority level</strong>.
        You start with <strong>one</strong> permission group — use &quot;Add permission group&quot; for more. Each card
        applies the same switches to every role or level selected in its dropdown. School Admin always has full access
        and is not listed.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          color="primary"
          size="small"
          onChange={requestModeChange}
          aria-label="Permission mode"
        >
          <ToggleButton value="ROLE">Role-based permissions</ToggleButton>
          <ToggleButton value="AUTHORITY">Authority level permissions</ToggleButton>
        </ToggleButtonGroup>
        {canEdit && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addBlock}>
            Add permission group
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {blocks.map((block) => (
          <Grid item xs={12} md={6} lg={4} key={block.id}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ pt: 2, flex: 1, overflow: 'auto' }}>
                {canEdit && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Remove permission group"
                      title={blocks.length <= 1 ? 'Add another group before removing this one' : 'Remove this group'}
                      onClick={() => removeBlock(block.id)}
                      disabled={blocks.length <= 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {renderKeySelect(block)}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 90 }}>View</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 90 }}>Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {STAFF_FEATURES.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell>{f.label}</TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={!!block.features[f.id]?.view}
                            onChange={(e) => {
                              setBlocks((prev) =>
                                prev.map((b) => {
                                  if (b.id !== block.id) return b;
                                  const nextF = { ...b.features[f.id], view: e.target.checked };
                                  if (!e.target.checked) nextF.edit = false;
                                  return {
                                    ...b,
                                    features: { ...b.features, [f.id]: nextF },
                                  };
                                }),
                              );
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {f.viewOnly ? (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          ) : (
                            <Switch
                              size="small"
                              checked={!!block.features[f.id]?.edit}
                              onChange={(e) => {
                                setBlocks((prev) =>
                                  prev.map((b) => {
                                    if (b.id !== block.id) return b;
                                    const nextF = {
                                      ...b.features[f.id],
                                      edit: e.target.checked,
                                      view: e.target.checked ? true : b.features[f.id]?.view,
                                    };
                                    return {
                                      ...b,
                                      features: { ...b.features, [f.id]: nextF },
                                    };
                                  }),
                                );
                              }}
                              disabled={!block.features[f.id]?.view}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                    Students list scope (this group)
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    disabled={!block.features.students?.view}
                    value={block.studentScope}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBlocks((prev) => prev.map((b) => (b.id === block.id ? { ...b, studentScope: v } : b)));
                    }}
                    helperText={!block.features.students?.view ? 'Enable Students View above to set scope.' : undefined}
                  >
                    {ACCESS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {canEdit && (
        <Box sx={{ mt: 3 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={updateMutation.isLoading}>
            Save
          </Button>
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Only one mode is active at a time. Saving uses {mode === 'ROLE' ? 'role-based' : 'authority-level'} maps and clears the other mode so they never stack. Add more cards if different roles or levels need different access. Students list scope applies when this group has Students view on.
      </Typography>
    </Box>
  );
};

export default StaffRolePermissions;
